import React, { useEffect, useRef, useState } from 'react';
import './Landing.css';
//images
import logoPiece1 from '../../Objects/AppLogo/logo_piece_1.png';
import logoPiece2 from '../../Objects/AppLogo/logo_piece_2.png';
import logoTag from '../../Objects/AppLogo/title logo tag.png';
//objs
import Icona from '../../Objects/Icona/Icona';
import Loading, { loadingColors } from '../../Objects/Loading/Loading';
import PrivacyPolicyLink from '../../Objects/PrivacyPolicyLink';
import CookiePolicyLink from '../../Objects/CookiePolicyLink';
import { unlockAudio } from '../../audio/audioManager';

/*
    LANDING DELLA HOME: overlay a tutto schermo con il logo animato sulla pedana.
    mode:
        "static" -> primo caricamento, tutto visibile subito (pagina prerenderizzata, il logo è l'LCP)
        "enter"  -> si torna dalla scelta del dispositivo: lo studio sfuma e il contenuto appare
        "leave"  -> dissolvenza verso il gioco (1 s), poi Home la smonta
    connecting: arrivo dal QR, al posto dei pulsanti c'è il caricamento
*/

//ritardo tra il click su "Gioca ora!" e onPlay: il tempo di vedere pressione, scatto e lampo
const PLAY_DELAY = 280;

//link iubenda di solo testo (senza il bottone nero di "iubenda-black")
const POLICY_LINK = "iubenda-nostyle no-brand iubenda-noiframe iubenda-embed";

//pezzi del logo: insieme formano il titolo "L'intesa vincente online!"
const PIECES = [
    { cls: "ld-p1", src: logoPiece1, alt: "L'intesa", w: 718, h: 158 },
    { cls: "ld-p2", src: logoPiece2, alt: "vincente", w: 718, h: 158 },
    { cls: "ld-tag", src: logoTag, alt: "online!", w: 304, h: 119 },
];

//il riflesso ha la forma del pezzo: la sua immagine fa da maschera
const sheenMask = (src) => ({
    WebkitMaskImage: `url("${src}")`,
    maskImage: `url("${src}")`,
});

const MODES = ["static", "enter", "leave"];

//pagina generata da react-snap: le animazioni restano sul primo fotogramma finché React non ricrea la landing
//(createRoot non idrata: senza questo il logo, già in movimento, tornerebbe di colpo all'inizio)
const SNAPSHOT = typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";

const Landing = ({ mode = "static", connecting = false, onPlay, onGuide }) => {
    const [pressed, setPressed] = useState(false);
    const pressedRef = useRef(false);   //blocca subito i doppi click (lo stato si aggiorna dopo)
    const timerRef = useRef(null);
    const onPlayRef = useRef(onPlay);   //nel timer serve la onPlay più recente
    const view = MODES.includes(mode) ? mode : "static";

    useEffect(() => {
        onPlayRef.current = onPlay;
    }, [onPlay]);

    //QUANDO TORNA "static" O "enter" IL PULSANTE SI PUÒ PREMERE DI NUOVO
    useEffect(() => {
        if (view !== "leave") {
            clearTimeout(timerRef.current);
            timerRef.current = null;
            pressedRef.current = false;
            setPressed(false);
        }
    }, [view]);

    //SMONTAGGIO: nessun timer rimasto in sospeso
    useEffect(() => {
        //link del QR: public/index.html nasconde i pulsanti della pagina prerenderizzata finché React non è partito.
        //Ora la landing mostra lo stato giusto (caricamento, o i pulsanti se il codice non è valido)
        document.documentElement.classList.remove("ld-qr");
        return () => {
            clearTimeout(timerRef.current);
            timerRef.current = null;
            pressedRef.current = false;
        };
    }, []);

    const play = () => {             //GIOCA ORA!
        if (pressedRef.current || view === "leave") {
            return;
        }
        pressedRef.current = true;
        //l'audio si sblocca solo dentro il gesto dell'utente (Safari/iOS): va chiamato subito
        unlockAudio().catch(() => {});
        setPressed(true);
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            if (onPlayRef.current) {
                onPlayRef.current();
            }
        }, PLAY_DELAY);
    };

    const guide = () => {            //LA GUIDA VINCENTE
        //dopo «Gioca ora!» la landing sta già uscendo: la guida non si apre sopra la dissolvenza
        if (view === "leave" || pressedRef.current) {
            return;
        }
        if (onGuide) {
            onGuide();
        }
    };

    return (
        <div className={"ld-root ld-" + view + (SNAPSHOT ? " ld-snap" : "")}>
            <div className="ld-page">
                <main className="ld-main">

                    {/* LOGO SULLA PEDANA */}
                    <div className="ld-head">
                        {/* luci dello studio: bagliore in alto, riflettore centrale e due fasci laterali */}
                        <div className="ld-decor" aria-hidden="true">
                            <div className="ld-glow"></div>
                            <div className="ld-beam ld-beam-c"><div></div></div>
                            <div className="ld-beam ld-beam-l"><div></div></div>
                            <div className="ld-beam ld-beam-r"><div></div></div>
                        </div>

                        <div className="ld-stage ld-fade">
                            <div className="ld-pedestal" aria-hidden="true">
                                <div className="ld-pedestal-rim"></div>
                                <div className="ld-pedestal-top"></div>
                                <div className="ld-shadow"></div>
                            </div>
                            <h1 className="ld-logo">
                                {PIECES.map((p) => (
                                    <span key={p.cls} className={"ld-piece " + p.cls}>
                                        <img src={p.src} alt={p.alt} width={p.w} height={p.h} />
                                        <span className="ld-sheen" aria-hidden="true" style={sheenMask(p.src)}><i></i></span>
                                    </span>
                                ))}
                                <span className="ld-spark" aria-hidden="true"></span>
                            </h1>
                        </div>
                    </div>

                    {/* TESTI E PULSANTI */}
                    <div className="ld-body ld-fade">
                        <div className="ld-texts">
                            <p className="ld-lead">Quanto vi capite <em>davvero</em>?</p>
                            <p className="ld-sub">
                                <strong>Preparatevi a scoprirlo!</strong> Con L'intesa vincente online giocherete come in TV, vivendo tutta l'adrenalina di Reazione a Catena.{" "}
                                <strong className="ld-punch">Non vince il più veloce, ma chi si capisce al volo.</strong>
                            </p>
                        </div>

                        {connecting ? (
                            <div className="ld-connecting" role="status">
                                <div aria-hidden="true">
                                    <Loading color={loadingColors["white"]} />
                                </div>
                                <p className="ld-connecting-txt">Collegamento alla partita…</p>
                            </div>
                        ) : (
                            <div className="ld-actions">
                                <div className={"ld-play-wrap" + (pressed ? " ld-pop" : "")}>
                                    <button type="button" className="ld-play" onClick={play}>
                                        <span className="ld-play-sheen" aria-hidden="true"><i></i></span>
                                        <span className="ld-play-txt">Gioca ora!</span>
                                    </button>
                                    <span className="ld-play-spark" aria-hidden="true"></span>
                                </div>
                                <button type="button" className="ld-guide" onClick={guide}>
                                    <Icona kind="book" size={48} className="ld-guide-icon" />
                                    La guida vincente
                                </button>
                            </div>
                        )}
                    </div>
                </main>

                {/* PIÈ DI PAGINA */}
                <footer className="ld-footer ld-fade">
                    <span>Creato da <a className="ld-author" href="https://tommasofaraci.it/" target="_blank" rel="noopener noreferrer">Tommaso Faraci</a> · Prodotto non ufficiale</span>
                    <span className="ld-policies">
                        <PrivacyPolicyLink className={POLICY_LINK} />
                        <span className="ld-dot" aria-hidden="true"> · </span>
                        <CookiePolicyLink className={POLICY_LINK} />
                    </span>
                </footer>
            </div>
        </div>
    );
}

export default Landing;
